import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Plus, Edit, Trash2, X, Upload, Download } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Student } from '../../types';
import { studentService } from '../../services/studentService';
import { StudentImportForm } from '../Students/StudentImportForm';

interface StudentSelectorProps {
  selectedStudentId: string | null;
  onStudentSelect: (studentId: string | null) => void;
  teacherId?: string;
  lessonFilter?: {
    grade?: string;
    className?: string;
  };
}

interface StudentFormData {
  name: string;
  grade: string;
  className: string;
}

export const StudentSelector: React.FC<StudentSelectorProps> = ({
  selectedStudentId,
  onStudentSelect,
  teacherId,
  lessonFilter
}) => {
  const { t } = useTranslation();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [formData, setFormData] = useState<StudentFormData>({
    name: '',
    grade: '',
    className: ''
  });
  const [showImportForm, setShowImportForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const loadStudents = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedStudents = await studentService.getStudents(teacherId);
      setStudents(fetchedStudents);
    } catch (error) {
      console.error('Error loading students:', error);
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  // Load students on component mount
  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const handleAddStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    try {
      const newStudent = await studentService.addStudent({
        name: formData.name.trim(),
        grade: formData.grade.trim(),
        teacherId: teacherId || 'default',
        status: 'active',
        enrollmentDate: new Date()
      });

      setStudents(prev => [...prev, newStudent]);
      setFormData({ name: '', grade: '', className: '' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding student:', error);
    }
  };

  const handleUpdateStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStudent || !formData.name.trim()) return;

    try {
      await studentService.updateStudent(editingStudent.id, {
        name: formData.name.trim(),
        grade: formData.grade.trim(),
        className: formData.className ? formData.className.trim() : undefined
      });

      setStudents(prev => prev.map(student => 
        student.id === editingStudent.id 
          ? { ...student, 
              name: formData.name.trim(), 
              grade: formData.grade.trim(), 
              className: formData.className.trim() 
            }
          : student
      ));

      setFormData({ name: '', grade: '', className: '' });
      setEditingStudent(null);
    } catch (error) {
      console.error('Error updating student:', error);
    }
  };

  const handleDeleteStudent = async (studentId: string) => {
    if (!window.confirm(t('studentSelector.confirmDelete'))) return;

    try {
      await studentService.deleteStudent(studentId);
      setStudents(prev => prev.filter(student => student.id !== studentId));
      
      // If the deleted student was selected, clear selection
      if (selectedStudentId === studentId) {
        onStudentSelect(null);
      }
    } catch (error) {
      console.error('Error deleting student:', error);
    }
  };

  const startEditing = (student: Student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name,
      grade: student.grade || '',
      className: student.className || ''
    });
  };

  const cancelEditing = () => {
    setEditingStudent(null);
    setFormData({ name: '', grade: '', className: '' });
  };



  const handleExportStudents = () => {
    const studentsData = students.map(student => ({
      name: student.name,
      grade: student.grade,
      className: student.className
    }));

    const dataStr = JSON.stringify(studentsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = 'students.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const selectedStudent = students.find(student => student.id === selectedStudentId);
  
  // Filter students based on search term and lesson filter
  const filteredStudents = students.filter(student => {
    // First apply lesson filter (grade and class matching)
    if (lessonFilter) {
      if (lessonFilter.grade && student.grade !== lessonFilter.grade) {
        return false;
      }
      if (lessonFilter.className && student.className !== lessonFilter.className) {
        return false;
      }
    }
    
    // Then apply search term filter
    return student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (student.grade && student.grade.toLowerCase().includes(searchTerm.toLowerCase())) ||
           (student.className && student.className.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current Selection */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
          <User className="w-4 h-4 mr-2" />
          {t('studentSelector.currentStudent')}
        </h3>
        
        {selectedStudent ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">{selectedStudent.name}</p>
              <p className="text-sm text-gray-600">
                {selectedStudent.grade && `${t('studentSelector.grade')}: ${selectedStudent.grade}`}
                {selectedStudent.className && selectedStudent.grade && ' • '}
                {selectedStudent.className && `${t('studentSelector.class')}: ${selectedStudent.className}`}
              </p>
            </div>
            <button
              onClick={() => onStudentSelect(null)}
              className="text-gray-400 hover:text-gray-600"
              title={t('studentSelector.clearSelection')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">{t('studentSelector.noStudentSelected')}</p>
        )}
      </div>

      {/* Student List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-900">{t('studentSelector.students')}</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowImportForm(true)}
              className="flex items-center text-sm text-primary-600 hover:text-primary-700"
              title={t('studentSelector.importStudents')}
            >
              <Upload className="w-4 h-4 mr-1" />
              {t('studentSelector.import')}
            </button>
            <button
              onClick={handleExportStudents}
              className="flex items-center text-sm text-primary-600 hover:text-primary-700"
              title={t('studentSelector.exportStudents')}
            >
              <Download className="w-4 h-4 mr-1" />
              {t('studentSelector.export')}
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center text-sm text-primary-600 hover:text-primary-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('studentSelector.addStudent')}
            </button>
          </div>
        </div>

        {/* Lesson Filter Indicator */}
        {lessonFilter && (lessonFilter.grade || lessonFilter.className) && (
          <div className="p-3 border-b border-gray-200 bg-blue-50">
            <div className="flex items-center space-x-2 text-sm text-blue-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">{t('studentSelector.lessonFilterActive')}:</span>
              {lessonFilter.grade && (
                <span className="px-2 py-1 bg-blue-100 rounded text-xs">
                  {t('studentSelector.grade')}: {lessonFilter.grade}
                </span>
              )}
              {lessonFilter.className && (
                <span className="px-2 py-1 bg-blue-100 rounded text-xs">
                  {t('studentSelector.class')}: {lessonFilter.className}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Search Filter */}
        <div className="p-3 border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('studentSelector.searchStudents')}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="max-h-64 overflow-y-auto">
          {filteredStudents.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              {searchTerm ? t('studentSelector.noSearchResults') : t('studentSelector.noStudents')}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredStudents.map((student) => (
                <div
                  key={student.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    selectedStudentId === student.id ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                  }`}
                  onClick={() => onStudentSelect(student.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{student.name}</p>
                      <p className="text-sm text-gray-600">
                        {student.grade && `${t('studentSelector.grade')}: ${student.grade}`}
                        {student.className && student.grade && ' • '}
                        {student.className && `${t('studentSelector.class')}: ${student.className}`}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(student);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title={t('studentSelector.editStudent')}
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteStudent(student.id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title={t('studentSelector.deleteStudent')}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Student Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-lg border border-gray-200 p-4"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900">{t('studentSelector.addNewStudent')}</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleAddStudent} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('studentSelector.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-field"
                  placeholder={t('studentSelector.enterName')}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('studentSelector.grade')}
                  </label>
                  <input
                    type="text"
                    value={formData.grade}
                    onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                    className="input-field"
                    placeholder={t('studentSelector.enterGrade')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('studentSelector.class')}
                  </label>
                  <input
                    type="text"
                    value={formData.className}
                    onChange={(e) => setFormData(prev => ({ ...prev, className: e.target.value }))}
                    className="input-field"
                    placeholder={t('studentSelector.enterClass')}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="btn-secondary"
                >
                  {t('studentSelector.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('studentSelector.addStudent')}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Student Form */}
      <AnimatePresence>
        {editingStudent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-lg border border-gray-200 p-4"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900">{t('studentSelector.editStudent')}</h3>
              <button
                onClick={cancelEditing}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleUpdateStudent} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('studentSelector.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-field"
                  placeholder={t('studentSelector.enterName')}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('studentSelector.grade')}
                  </label>
                  <input
                    type="text"
                    value={formData.grade}
                    onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                    className="input-field"
                    placeholder={t('studentSelector.enterGrade')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('studentSelector.class')}
                  </label>
                  <input
                    type="text"
                    value={formData.className}
                    onChange={(e) => setFormData(prev => ({ ...prev, className: e.target.value }))}
                    className="input-field"
                    placeholder={t('studentSelector.enterClass')}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="btn-secondary"
                >
                  {t('studentSelector.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('studentSelector.updateStudent')}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Unified Student Import Form */}
      <StudentImportForm
        showModal={showImportForm}
        setShowModal={setShowImportForm}
        onStudentsImported={(importedStudents) => {
          // Reload students to show the newly imported ones
          loadStudents();
        }}
        onClose={() => {
          // Modal closed, no cleanup needed
        }}
      />
    </div>
  );
};
